import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Spin, Modal, Checkbox } from "antd";

const Slideshow = ({ setUsername, setRoomname, imagesData = [], showButton, voteEvent }) => {
  const [deck, setDeck] = useState(imagesData);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(
    JSON.parse(localStorage.getItem("doNotShowAgain")) || false
  );

  // card AR cache: { [cardUUID]: number }
  const [aspectById, setAspectById] = useState({});
  const [, setTick] = useState(0); // re-render during drag

  useEffect(() => setDeck(imagesData || []), [imagesData]);
  useEffect(() => localStorage.setItem("doNotShowAgain", JSON.stringify(doNotShowAgain)), [doNotShowAgain]);

  const STACK_SIZE = useMemo(() => {
    const n = deck.length || 0;
    return Math.max(4, Math.min(6, n || 4));
  }, [deck.length]);

  const topCard = deck[0] || null;

  // Preload + AR mérés a látható kártyákra
  const visibleCards = useMemo(() => deck.slice(0, STACK_SIZE), [deck, STACK_SIZE]);

  useEffect(() => {
    visibleCards.forEach((c) => {
      if (!c || aspectById[c.cardUUID]) return;
      const im = new Image();
      im.src = c.URL;
      im.onload = () => {
        const ar = im.naturalWidth && im.naturalHeight ? im.naturalWidth / im.naturalHeight : 2 / 3;
        setAspectById((p) => ({ ...p, [c.cardUUID]: ar }));
      };
    });
  }, [visibleCards, aspectById]);

  // Top kép betöltöttség a spinnerhez
  useEffect(() => {
    if (!topCard) return;
    setImagesLoaded(false);
    const img = new Image();
    img.src = topCard.URL;
    img.onload = () => setImagesLoaded(true);
  }, [topCard]);

  // ----- swipe / drag -----
  const dragRef = useRef({ isDragging: false, startX: 0, startY: 0, dx: 0, dy: 0 });
  const deckViewportRef = useRef(null);
  const frameRef = useRef(null);

  const H_THRESHOLD = 80;   // bal/jobb → cycle
  const V_THRESHOLD = 100;  // felfelé → submit

  const onPointerDown = (e) => {
    if (!topCard) return;
    const { clientX, clientY } = "touches" in e ? e.touches[0] : e;
    dragRef.current = { isDragging: true, startX: clientX, startY: clientY, dx: 0, dy: 0 };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.isDragging) return;
    const { clientX, clientY } = "touches" in e ? e.touches[0] : e;
    dragRef.current.dx = clientX - dragRef.current.startX;
    dragRef.current.dy = clientY - dragRef.current.startY;
    setTick((t) => t + 1);
  };
  const cycleTopToBottom = () => setDeck((d) => (d.length > 1 ? [...d.slice(1), d[0]] : d));
  const onPointerUp = () => {
    if (!dragRef.current.isDragging) return;
    const { dx, dy } = dragRef.current;
    dragRef.current.isDragging = false;

    if (Math.abs(dy) > Math.abs(dx) && dy < -V_THRESHOLD) {
      // felfelé → submit
      dragRef.current.dx = 0;
      dragRef.current.dy = -((deckViewportRef.current?.clientHeight || window.innerHeight) * 0.9);
      setTick((t) => t + 1);
      setTimeout(() => {
        dragRef.current.dx = 0; dragRef.current.dy = 0; setTick((t) => t + 1);
        handleChoose();
      }, 140);
      return;
    }

    if (Math.abs(dx) > H_THRESHOLD) {
      // vízszintes → cycle
      const w = deckViewportRef.current?.clientWidth || window.innerWidth;
      const dir = Math.sign(dx) || 1;
      dragRef.current.dx = dir * (w * 0.9);
      setTick((t) => t + 1);
      setTimeout(() => {
        dragRef.current.dx = 0; dragRef.current.dy = 0; cycleTopToBottom(); setTick((t) => t + 1);
      }, 160);
      return;
    }

    dragRef.current.dx = 0; dragRef.current.dy = 0; setTick((t) => t + 1);
  };

  const handleChoose = () => {
    if (!topCard) return;
    if (doNotShowAgain) handleConfirmationOk();
    else setConfirmationVisible(true);
  };
  const handleConfirmationOk = () => { setConfirmationVisible(false); if (topCard) voteEvent(topCard); };

  // ----- layout (container-relative, AR-tartás) -----
  const rootStyle = { width: "100%", maxWidth: 480, margin: "0 auto", padding: "12px 12px 16px", boxSizing: "border-box" };
  const deckViewportStyle = {
    position: "relative",
    width: "100%",
    height: "clamp(360px, 68vh, 720px)",
    overflow: "hidden",
    borderRadius: 20,
    touchAction: "none",
  };
  const CARD_INSET = 12;
  const frameStyle = {
    position: "absolute",
    left: CARD_INSET, top: CARD_INSET, right: CARD_INSET, bottom: CARD_INSET,
  };

  const cardBase = {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 8px 24px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.25)",
    background: "#111",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    willChange: "transform",
    userSelect: "none",
    WebkitUserSelect: "none",
  };

  const imageStyle = {
    width: "100%",
    height: "100%",
    objectFit: "contain", // <-- NINCS vágás, AR megmarad
    pointerEvents: "none",
    background: "#0b0b0b", // letterbox háttér
  };

  const centerOverlay = { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" };

  // kis segéd a FIT-hez
  const computeCardSize = (ar) => {
    const fw = frameRef.current?.clientWidth || 320;
    const fh = frameRef.current?.clientHeight || 480;
    // illesztés a frame-be AR megtartásával
    let w = fw, h = w / ar;
    if (h > fh) { h = fh; w = h * ar; }
    // kis “lélegző” margó
    w *= 0.98; h *= 0.98;
    return { w, h };
  };

  const renderCard = (card, idx) => {
    const isTop = idx === 0;
    const ar = aspectById[card.cardUUID] || 2 / 3; // default AR
    const { w, h } = computeCardSize(ar);

    const stackOffset = idx * 8;
    const scale = 1 - idx * 0.02; // finomabb, hogy biztosan a frame-ben maradjon

    const dx = isTop ? dragRef.current.dx : 0;
    const dy = isTop ? dragRef.current.dy : 0;
    const rotate = isTop ? dx * 0.05 : 0;

    const style = {
      ...cardBase,
      width: `${w}px`,
      height: `${h}px`,
      zIndex: visibleCards.length - idx,
      transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy + stackOffset}px)) rotate(${rotate}deg) scale(${scale})`,
      transition: dragRef.current.isDragging && isTop ? "none" : "transform 180ms ease",
      opacity: isTop ? 1 : 0.96 - idx * 0.08,
    };

    const handlers = isTop ? {
      onMouseDown: onPointerDown, onMouseMove: onPointerMove, onMouseUp: onPointerUp, onMouseLeave: onPointerUp,
      onTouchStart: onPointerDown, onTouchMove: onPointerMove, onTouchEnd: onPointerUp,
    } : {};

    return (
      <div key={card.cardUUID} style={style} {...handlers}>
        {!imagesLoaded && isTop && <div style={centerOverlay}><Spin size="large" /></div>}
        <img src={card.URL} alt={`Card ${card.cardUUID}`} style={imageStyle} draggable={false} />
      </div>
    );
  };

  return (
    <div style={rootStyle}>
      <div ref={deckViewportRef} style={deckViewportStyle}>
        <div ref={frameRef} style={frameStyle}>
          {visibleCards.map((card, idx) => renderCard(card, idx))}
        </div>
      </div>

      {showButton && topCard && (
        <div style={{ marginTop: 16 }}>
          <Button type="primary" onClick={handleChoose} block size="large" style={{ height: 48, fontWeight: 600 }}>
            I choose this!
          </Button>
        </div>
      )}

      <Modal
        title="Are you sure about this?"
        open={confirmationVisible}
        onOk={handleConfirmationOk}
        onCancel={() => setConfirmationVisible(false)}
        okText="Confirm"
        cancelText="No"
      >
        <p>Confirm to submit this card.</p>
        <Checkbox checked={doNotShowAgain} onChange={(e) => setDoNotShowAgain(e.target.checked)}>
          Do not show this again
        </Checkbox>
      </Modal>
    </div>
  );
};

export { Slideshow };
